import { useCallback, useEffect, useRef, useState } from "react";
import { cancelGeneration, getGenerationStatus } from "@/lib/api";

// 10 min upper bound — even the slowest renders (video, upscale) finish well
// before this, so exceeding it means the backend lost the job. We'd rather
// surface "something went wrong" to the user than keep a zombie poller alive.
const MAX_POLL_MS = 10 * 60 * 1000;

// After N consecutive network errors, assume a real outage and stop. Without
// this the UI spins forever while the backend is down, and each tick fires
// another request that piles up in the browser.
const MAX_CONSECUTIVE_ERRORS = 5;

// 3s matches what the tool views used before. Fast enough that results feel
// immediate; slow enough that we don't hammer the status endpoint.
const DEFAULT_INTERVAL_MS = 3000;

type PollCallbacks = {
  // `status` carries the full generation row so callers can read
  // `meta.all_outputs` for multi-output flows (e.g. Wiro VTO «both»
  // returns image + video). `resultUrl` stays the primary URL for
  // backward compatibility — older callers can ignore the second arg.
  onComplete: (resultUrl: string, status?: Record<string, unknown>) => void;
  onFailed: (error: string | null) => void;
  onTimeout: () => void;
  onCancelled?: () => void;
  intervalMs?: number;
};

/**
 * Poll a generation task until it completes, fails, or we give up.
 *
 * The previous per-view pattern (bare `setInterval`) polled forever if a job
 * got stuck server-side and piled up requests when the network dropped. This
 * hook bounds both.
 *
 * Returns `{ cancel, isCancelling }` so the view can render a cancel button that
 * marks the generation FAILED on the server and refunds credits.
 */
export function useGenerationPolling(
  taskId: string | null,
  { onComplete, onFailed, onTimeout, onCancelled, intervalMs = DEFAULT_INTERVAL_MS }: PollCallbacks,
) {
  const cbRef = useRef({ onComplete, onFailed, onTimeout, onCancelled });
  cbRef.current = { onComplete, onFailed, onTimeout, onCancelled };
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (!taskId) return;

    const startedAt = Date.now();
    let consecutiveErrors = 0;
    let cancelled = false;

    const interval = setInterval(async () => {
      if (cancelled) return;

      if (Date.now() - startedAt > MAX_POLL_MS) {
        clearInterval(interval);
        cbRef.current.onTimeout();
        return;
      }

      try {
        const status = await getGenerationStatus(taskId);
        consecutiveErrors = 0;

        if (status.status === "COMPLETED" && status.result_url) {
          clearInterval(interval);
          cbRef.current.onComplete(status.result_url, status);
        } else if (status.status === "FAILED") {
          clearInterval(interval);
          if (status.error === "cancelled_by_user" && cbRef.current.onCancelled) {
            cbRef.current.onCancelled();
          } else {
            cbRef.current.onFailed(status.error ?? null);
          }
        }
      } catch {
        consecutiveErrors += 1;
        if (consecutiveErrors >= MAX_CONSECUTIVE_ERRORS) {
          clearInterval(interval);
          cbRef.current.onTimeout();
        }
      }
    }, intervalMs);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [taskId, intervalMs]);

  const cancel = useCallback(async () => {
    if (!taskId || isCancelling) return;
    setIsCancelling(true);
    try {
      await cancelGeneration(taskId);
      if (cbRef.current.onCancelled) cbRef.current.onCancelled();
      else cbRef.current.onFailed("cancelled_by_user");
    } finally {
      setIsCancelling(false);
    }
  }, [taskId, isCancelling]);

  return { cancel, isCancelling } as const;
}
